import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React, { FC } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useGetRepairOrdersQuery,
  useUpdateRepairOrderMutation
} from '../../redux/api'
import { RepairOrderExpanded } from '../../sdk'
import { ControlledMuiTextField } from '../FormField'

interface Props {
  open: boolean
  onClose: () => void
}

interface FormInputs {
  deleteReason: string
}

const DeleteDialog: FC<Props> = ({ open, onClose }) => {
  const { control, handleSubmit } = useForm<FormInputs>({
    defaultValues: {
      deleteReason: ''
    }
  })
  const { id } = useParams()
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: repairOrderList } = useGetRepairOrdersQuery({
    authorization: accessToken,
    shop: shopId,
    repairOrder: [id!]
  })
  const [updateRepairOrder] = useUpdateRepairOrderMutation()

  const repairOrder =
    repairOrderList && repairOrderList.length > 0 && repairOrderList[0]
  const entry = repairOrder ? repairOrder.entry! : null
  const archived = entry && entry.info!.archived

  const handleDelete = (reason: string) => {
    const entry = (repairOrder as RepairOrderExpanded).entry!
    updateRepairOrder({
      authorization: accessToken,
      repairOrderEntry: {
        ...entry,
        info: {
          ...entry.info,
          archived: new Date(),
          archiveReason: reason
        }
      }
    })
  }

  const handleUndelete = () => {
    const entry = (repairOrder as RepairOrderExpanded).entry!
    updateRepairOrder({
      authorization: accessToken,
      repairOrderEntry: {
        ...entry,
        info: {
          ...entry.info,
          archived: undefined,
          archiveReason: undefined
        }
      }
    })
  }

  const onSubmit: SubmitHandler<FormInputs> = ({ deleteReason }) => {
    handleDelete(deleteReason)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose}>
      {repairOrder && (
        <>
          <DialogTitle>
            Confirm {archived ? 'Undelete' : 'Delete'}{' '}
            {repairOrder.entry!.info!.isEstimate ? 'Estimate' : 'Repair Order'}
          </DialogTitle>
          {archived ? (
            <>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to undelete this{' '}
                  {repairOrder.entry!.info!.isEstimate
                    ? 'estimate'
                    : 'repair order'}
                  ?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  onClick={() => {
                    handleUndelete()
                    onClose()
                  }}>
                  OK
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogContent>
                <Box sx={{ paddingTop: 1 }}>
                  <form id="confirmDelete" onSubmit={handleSubmit(onSubmit)}>
                    <ControlledMuiTextField
                      label="Delete Reason"
                      name="deleteReason"
                      control={control}
                      required
                    />
                  </form>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button type="submit" form="confirmDelete">
                  Submit
                </Button>
              </DialogActions>
            </>
          )}
        </>
      )}
    </Dialog>
  )
}

export default DeleteDialog
